import axios from 'axios';
import moment from 'moment';
import history from './routes/history'
import CookiesManager from './CookiesManager'

/**********************************************************************
** API Class
*
* Singleton class that:
* - Keeps the authentication token, userId and the logged user.
* - Enables to send all the requests to the server API.
*
* All requests must have the authentication token as a GET parameter,
* except the login request.
***********************************************************************/
export const VERIFICATION_VIA_EMAIL = 'mail';
export const VERIFICATION_VIA_SMS   = 'phone';

export default class API
{
    static TESTS = false;

    static API_VERSION = 'v1/';
    static URL_API_TESTS = 'https://devapp.homyhub.com/' + API.API_VERSION;
    static URL_API = 'https://app.homyhub.com/' + API.API_VERSION;

    static URL = (API.TESTS) ? API.URL_API_TESTS : API.URL_API;

    static instance = null;

	_token = null;
	_userId = null;
    _currentUser = null;
    
    _initialNavComponentId = null;
    _doNotIntercept = false;
    
    _houses = []

    static CACHED_HOUSES_KEY = '_houses'
    static CACHED_HOUSES_TTL = 7 * 24 * 60 * 60 * 1000; // ms


    constructor()
    {
        axios.defaults.timeout = (API.TESTS) ? 30000 : 20000

        this.loadLoginData();
        this.setInterceptors();
    }
    static getInstance()
    {
        if (this.instance == null)
            this.instance = new API();

        return this.instance;
    }
    
    static isProd()
    {
        return API.URL === API.URL_API
    }


	getToken() { return this._token; }
	getUserId() { return this._userId; }
    
    getDeviceId()
    {
        let deviceId = CookiesManager.getInstance().get('deviceId')
        
        if (deviceId == null)
        {
            deviceId = Math.random()
            CookiesManager.getInstance().set('deviceId', deviceId)
        }
        
        return deviceId
    }
    getDeviceType() { return 'web' }

    saveLoginData()
    {
        const expires = "; expires=Fri, 31 Dec 2030 23:59:59 GMT; path=/"
        document.cookie = "_token=" + this._token + expires
        document.cookie = "_userId=" + this._userId + expires
        
        localStorage.setItem('_token', this._token)
        localStorage.setItem('_userId', this._userId)
    }
    loadLoginData()
    {
        this._token = this.getCookie('_token')
        this._userId = parseInt(this.getCookie('_userId'))
        
        if (this._token === null || this._token?.length === 0 ||
            this._userId === null || this._userId?.length === 0)
        {
            this._token = localStorage.getItem('_token')
            this._userId = parseInt(localStorage.getItem('_userId'))
        }
    }
    getCookie(name)
    {
        const value = `; ${document.cookie}`
        const parts = value.split(`; ${name}=`)
        if (parts.length === 2) return parts.pop().split(';').shift()
    }
    isTesla()
    {
        let isTesla = window.navigator.userAgent.toLowerCase().includes('tesla') ||
                      localStorage.getItem('_tesla', false) ||
                      this.getCookie('_tesla')
        
        return isTesla
    }

	hasActiveSession()
	{
		var hasActiveSession = false;

		this.loadLoginData()

        hasActiveSession = (this._token != null && this._userId != null)
        
        console.log('hasActiveSession: ' + hasActiveSession)
        
        return hasActiveSession
    }
    setExternalAccessToken(accessToken, userId)
    {
        console.log('setExternalAccessToken', accessToken, userId)
        
        if (accessToken == null || userId == null)
            return
        
        this.deleteThingsInLogout()
        
        this._token = accessToken
        this._userId = parseInt(userId)
        
        this.saveLoginData()
    }

    /**
     *
     * @param password
     */
    validateUser(password) {

        return axios.post(API.URL + 'users/login',
            {
                email: localStorage.getItem('_email'),
                password: password
            })
            .then(response =>
            {
                return response;
            })
            .catch(error =>
            {
                return Promise.reject(error);
            });

    }

    setInterceptors()
    {        
        axios.interceptors.response.use((response) =>
        {
            return response;
        },
        this.onInterceptResponseError.bind(this));
    }
    onInterceptResponseError(error)
    {
        if (error.response != null &&
            !this._doNotIntercept &&
            error.response.status === 401)
        {
            this._doNotIntercept = true
            
            this.getCurrentUser(true)
            .then(user => { this._doNotIntercept = false })
            .catch(err =>
            {
                if (err.response.status === 401)
                {
                    // No authorization -> log out
                    this.logout()
                    
                    // Go to landing
                    history.push('/')
                }
                else
                    this._doNotIntercept = false
            })
        }
        //else
            //this._doNotIntercept = false
        
        return Promise.reject(error)
    }
    
    
    setMainHouses(houses)
    {
        this._houses = houses
    }

    /*** HOUSES LOCAL CACHE ***/
    /**
     * Saves the houses in localStorage (not in a cookie: the payload is way
     * over the ~4KB cookie limit and it would travel in every request).
     * Cleared on logout in deleteThingsInLogout().
     */
    saveCachedHouses(houses)
    {
        if (houses == null || houses.length === 0)
            return

        const trimmed = this._trimHousesForCache(houses)

        // Free the previous entry first: it is stale anyway, and its space
        // counts against the quota we are about to fill.
        this.deleteCachedHouses()

        // Store as many houses as localStorage accepts. There is no useful
        // fixed cap: how many fit depends on the doors per house and on
        // whatever else already lives in localStorage, so probe for it.
        if (this._writeHousesCache(trimmed))
            return

        // Largest amount known to fit (lo) vs smallest known not to fit (hi).
        let lo = 0
        let hi = trimmed.length

        while (hi - lo > 1)
        {
            const mid = Math.floor((lo + hi) / 2)

            if (this._writeHousesCache(trimmed.slice(0, mid)))
                lo = mid
            else
                hi = mid
        }

        // A failed setItem writes nothing, so the entry left behind is the one
        // from the last successful probe, which is the largest that fitted.
        if (lo === 0)
        {
            console.log('saveCachedHouses: no room in localStorage')
            this.deleteCachedHouses()
        }
    }
    /**
     * Writes the cache entry. Returns false when it does not fit
     * (QuotaExceededError) or cannot be serialized.
     */
    _writeHousesCache(houses)
    {
        try
        {
            const cache =
            {
                userId: this._userId,
                token: this._token,
                savedAt: Date.now(),
                houses
            }

            localStorage.setItem(API.CACHED_HOUSES_KEY, JSON.stringify(cache))
            return true
        }
        catch(e)
        {
            return false
        }
    }
    /**
     * Keeps only the fields the garages list needs to paint itself. This is a
     * whitelist on purpose: the raw doors carry their whole PermissionNode
     * (which embeds a copy of its PermissionHouse) and their virtualRemotes
     * twice over, with a subscription object each. None of that is read while
     * rendering a DoorCard, and it is what blows the localStorage quota.
     */
    _trimHousesForCache(houses)
    {
        return houses.map(house => (
        {
            ...house,
            doors: house.doors?.map(door => (
            {
                // DoorCard
                id: door.id,
                name: door.name,
                imageURL: door.imageURL,
                // DoorStatusPill
                roleType: door.roleType,
                isOpen: door.isOpen,
                changeStatusAt: door.changeStatusAt,
                node: (door.node != null) ? { batteryStatus: door.node.batteryStatus } : null,
                myPermission:
                {
                    startDate: door.myPermission?.startDate,
                    endDate: door.myPermission?.endDate,
                    permissionDays: door.myPermission?.permissionDays
                },
                // VRExpirationTag
                houseId: door.houseId,
                assignedVirtualRemote: door.assignedVirtualRemote
            }))
        }))
    }
    /**
     * Returns the cached houses, or null if there is no valid cache.
     * The cache is discarded when it belongs to another user, when the token
     * changed (the doors imageURL embeds the access_token, so the old URLs
     * would fail) or when it is older than CACHED_HOUSES_TTL.
     */
    getCachedHouses()
    {
        try
        {
            const raw = localStorage.getItem(API.CACHED_HOUSES_KEY)
            if (raw == null)
                return null

            const cache = JSON.parse(raw)

            if (cache?.houses == null || cache.houses.length === 0 ||
                cache.userId !== this._userId ||
                cache.token !== this._token ||
                (Date.now() - cache.savedAt) > API.CACHED_HOUSES_TTL)
            {
                this.deleteCachedHouses()
                return null
            }

            return cache.houses
        }
        catch(e)
        {
            console.log('getCachedHouses', e)
            this.deleteCachedHouses()
            return null
        }
    }
    deleteCachedHouses()
    {
        localStorage.removeItem(API.CACHED_HOUSES_KEY)
    }
    /*** HOUSES LOCAL CACHE ***/
    getDoorIdFromActuatorId(actuatorId)
    {
        let doorId = null
        
        for (let i = 0; i < this._houses.length && doorId == null; i++)
        {
            let house = this._houses[i]
            if (house.nodes != null)
            {
                for (let j = 0; j < house.nodes.length && doorId == null; j++)
                {
                    let node = house.nodes[j]
                    for (let k = 0; k < node.doors.length && doorId == null; k++)
                    {
                        let door = node.doors[k]
                        if (door.actuatorId != null && door.actuatorId == actuatorId)
                            doorId = door.id
                    }
                }
            }
            else if (house.doors != null)
            {
                for (let k = 0; k < house.doors.length && doorId == null; k++)
                {
                    let door = house.doors[k]
                    if (door.actuatorId != null && door.actuatorId == actuatorId)
                        doorId = door.id
                }
            }
        }
        
        return doorId
    }
    getDoorIdFromSensorId(sensorId)
    {
        let doorId = null
        
        for (let i = 0; i < this._houses.length && doorId == null; i++)
        {
            let house = this._houses[i]
            if (house.nodes != null)
            {
                for (let j = 0; j < house.nodes.length && doorId == null; j++)
                {
                    let node = house.nodes[j]
                    for (let k = 0; k < node.doors.length && doorId == null; k++)
                    {
                        let door = node.doors[k]
                        if (door.sensorId != null && door.sensorId == sensorId)
                            doorId = door.id
                    }
                }
            }
            else if (house.doors != null)
            {
                for (let k = 0; k < house.doors.length && doorId == null; k++)
                {
                    let door = house.doors[k]
                    if (door.sensorId != null && door.sensorId == sensorId)
                        doorId = door.id
                }
            }
        }
        
        return doorId
    }
    getDoorIdsArray(houses)
    {
        let doorIds = []
        let finalHouses = (houses != null) ? houses : this._houses
        
        finalHouses.forEach(house =>
        {
            if (house.doors != null && house.doors.length > 0)
            {
                house.doors.forEach(door =>
                {
                    if (door.id != null)
                        doorIds.push(door.id)
                })
            }
            if (house.nodes != null && house.nodes.length > 0)
            {
                house.nodes.forEach(node =>
                {
                    if (node.doors != null && node.doors.length > 0)
                    {
                        node.doors.forEach(door =>
                        {
                            if (door.id != null)
                                doorIds.push(door.id)
                        })
                    }
                })
            }
        })
        
        return doorIds
    }


	/** Login Requests **/

    /**
    * Login request
    * Endpoint: /users/login
    * Type: POST
    *
    * @param {string} email The email to log in
    * @param {password} password The password to log in
    *
    * @returns {Promise}
    */
    login(email, password)
    {
        return axios.post(API.URL + 'users/login',
        {
            email: email,
            password: password,
            deviceId: this.getDeviceId(),
            deviceType: this.getDeviceType()
        })
        .then(response =>
        {
			if (response.data != null && response.data.id != null && response.data.userId != null)
			{
				this._token = response.data.id;
				this._userId = parseInt(response.data.userId)

				this.saveLoginData();
                // this.sendAppInstallation();
			}

            return response;
        })
        .catch(error =>
        {
            return Promise.reject(error);
        });
    }

    /** TO REMOVE - NOT IN USE
    * Forgot password request
    * Endpoint: /users/reset
    * Type: POST
    *
    * @param {string} email The email to send the password reset
    *
    * @returns {Promise}
    */
    forgotPassword(email)
    {
        return axios.post(API.URL + 'users/reset',
        {
            email: email
        })
        .then(response =>
        {
            return response;
        })
        .catch(error =>
        {
            return Promise.reject(error);
        });
    }

    /**
    * Create account request
    * Endpoint: /users
    * Type: POST
    *
    * @param {string} name
    * @param {string} lastName
    * @param {string} phone with the country code and a + (ex: +34)
    * @param {string} email
    * @param {string} pass
    *
    * @returns {Promise}
    */
    createAccount(name, lastName, phone, email, pass)
    {
        return axios.post(API.URL + 'users',
        {
            name: name,
            lastName: lastName,
            phoneNumber: phone,
            email: email,
            password: pass
        })
        .then(response =>
        {
            return response;
        })
        .catch(error =>
        {
            return Promise.reject(error);
        });
    }
    
    /**
    * Send verification code
    * Endpoint: /VerificationCodes/resend
    * Type: POST
    *
    * @param {number} userId
    * @param {string} via [mail|phone]
    *
    * @returns {Promise}
    */
    sendVerificationCode(userId, via)
    {
        return axios.post(API.URL + 'VerificationCodes/resend',
        `userId=${userId}&via=${via}`)
        .then(response =>
        {
            return response;
        })
        .catch(error =>
        {
            return Promise.reject(error);
        });
    }
    
    /**
    * Generate a new verification code
    * Endpoint: /VerificationCodes/generate-delete-code
    * Type: POST
    *
    * @param {string} element // door, house, node, hub
    * @param {string} elementId // doorId, houseId, nodeId, hubId
    *
    * @returns {Promise}
    */
   generateVerificationDeleteCode(element, elementId)
   {
       return axios.post(API.URL + 'VerificationCodes/generate-delete-code',
       `element=${element}&elementId=${elementId}`)
       .then(response =>
       {
           return response;
       })
       .catch(error =>
       {
           return Promise.reject(error);
       });
   }
    
    /**
    * Verify by code
    * Endpoint: /users/verify-by-code
    * Type: POST
    *
    * @param {number} userId
    * @param {string} code
    * @param {string} emailOrPhone
    *
    * @returns {Promise}
    */
    verifyUserByCode(userId, code, emailOrPhone)
    {
        return axios.post(API.URL + 'users/verify-by-code',
        `userId=${userId}&code=${code}&data=${emailOrPhone.replace('+','%2B')}`)
        .then(response =>
        {                
            return response;
        })
        .catch(error =>
        {
            return Promise.reject(error);
        });
    }
    
    /**
    * Generate a new verification code
    * Endpoint: /users/generate-verification-code
    * Type: GET
    *
    * @param {string} email
    *
    * @returns {Promise}
    */
    generateVerificationCode(email)
    {
        return axios.get(API.URL + 'users/generate-verification-code',
        {
			params:
			{
            	email: email
			}
        })
        .then(response =>
        {
            return response;
        })
        .catch(error =>
        {
            return Promise.reject(error);
        });
    }
    
    /**
    * Verify by code
    * Endpoint: /users/verify-by-code
    * Type: POST
    *
    * @param {number} userId
    * @param {string} code
    *
    * @returns {Promise}
    */
    resumeSignup(userId, date, email, phone)
    {
        let data = `userId=${userId}&date=${date}`;
        if (email != null) data += `&email=${email}`;
        if (phone != null) data += `&phone=${phone.replace('+','%2B')}`;
        
        return axios.post(API.URL + 'users/resume-signup', data)
        .then(response =>
        {                
            return response;
        })
        .catch(error =>
        {
            return Promise.reject(error);
        });
    }
    
    /**
    * Updates a user's phone (sends a 6 digit code), no token required.
    * Endpoint: /users/change-phone-via-email
    * Type: POST
    *
    * @param {string} newPhone
    * @param {string} email
    *
    * @returns {Promise}
    */
    changePhoneViaEmail(newPhone, email)
	{
		return axios.post(`${API.URL}users/change-phone-via-email`,
        `phone=${newPhone.replace('+', '%2B')}&email=${email}`)
        .then(response =>
        {
            return response;
        })
        .catch(error =>
        {
            return Promise.reject(error);
        });
	}
    
    /**
    * Updates a user's phone (verifies the 6 digit code), no token required.
    * Endpoint: /users/verify-change-phone-via-email
    * Type: POST
    *
    * @param {string} phone
    * @param {string} email
    * @param {string} code
    *
    * @returns {Promise}
    */
    verifyChangePhoneViaEmail(phone, email, code)
	{
		return axios.post(`${API.URL}users/verify-change-phone-via-email`,
        `phone=${phone.replace('+', '%2B')}&email=${email}&code=${code}`)
        .then(response =>
        {
            return response;
        })
        .catch(error =>
        {
            return Promise.reject(error);
        });
	}
    
    

    /**
    * Get current (authenticated) user request
    * Endpoint: /users/{userId}
    * Type: GET
    *
    * @returns {Promise} A promise that returns the user or an error if rejected
    */
    getCurrentUser(forceDownload)
	{
		return new Promise((resolve, reject) =>
		{
            if (forceDownload != null && forceDownload)
            {
                return axios.get(API.URL + 'users/' + this._userId,
                {
                    params:
                    {
                        access_token: this._token
                    }
                })
                .then(response =>
                {
                    this._currentUser = response.data;
                    this._setUserCountryCode();
                    resolve(response.data);
                })
                .catch(error =>
                {
                    reject(error);
                });
            }
            else
            {
                if (this._currentUser == null)
                {
        			if (this._userId == null)
        			{
        				this.loadLoginData(() =>
        				{
        					axios.get(API.URL + 'users/' + this._userId,
        			        {
        						params:
        						{
        			            	access_token: this._token
        						}
        			        })
        			        .then(response =>
        			        {
                                this._currentUser = response.data;
                                this._setUserCountryCode();
        			            resolve(response.data);
        			        })
        			        .catch(error =>
        			        {
        			            reject(error);
        			        });
        				});
        			}
        			else
        			{
        				return axios.get(API.URL + 'users/' + this._userId,
        				{
        					params:
        					{
        						access_token: this._token
        					}
        				})
        				.then(response =>
        				{
                            this._currentUser = response.data;
                            this._setUserCountryCode();
        					resolve(response.data);
        				})
        				.catch(error =>
        				{
        					reject(error);
        				});
        			}
                }
                else
                    resolve(this._currentUser);
            }
        });
	}

    /**
    * Logout request
    * Endpoint: /users/logout
    * Type: POST
    *
    * @returns {Promise}
    */
    logout()
    {
        console.log('logout')
        let theToken = this._token
        let isTesla = this.isTesla()
        this.deleteThingsInLogout();
        
        if (isTesla)
            return new Promise((resolve) => { resolve() })
        
        console.log('Sending logout request')
        return axios.post(API.URL + 'users/logout?access_token=' + theToken)
        .then(response =>
        {
			return response;
        })
        .catch(error =>
        {
            return Promise.reject(error);
        });
    }

    deleteThingsInLogout()
	{
		this._token = null
		this._userId = null
        this._currentUser = null

        localStorage.removeItem('_token')
        localStorage.removeItem('_userId')
        localStorage.removeItem('_tesla')
        this.deleteCachedHouses()
        
        const expirePast = "; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/";
        document.cookie = "_token=" + expirePast;
        document.cookie = "_userId=" + expirePast;
        document.cookie = "_tesla=" + expirePast;
    }

    _setUserCountryCode()
    {
        // const phoneUtil = require('google-libphonenumber').PhoneNumberUtil.getInstance();
        // const number = phoneUtil.parseAndKeepRawInput(this._currentUser.phoneNumber);
        // this._currentUser.phoneCountryCode = number.getCountryCode();
    }
    
    
    deleteUser()
    {
        let params =
        {
            access_token: this._token
        }
        
		return axios.get(API.URL + 'users/delete', { params })
    }
    verifyDeleteUser(code)
    {
        let params =
        {
            access_token: this._token,
            code
        }
        
		return axios.get(API.URL + 'users/verify-delete-user', { params })
    }


	/********** Garages Requests **********/

    /**
    * Get houses and all the necessary info to display the doors cards
    * New Endpoint: /permissions
    * Old Endpoint: /doors/get-doors-by-house
    * Type: GET
    *
    * @returns {Promise}
    */
	getHouses()
	{
		return axios.get(API.URL + 'permissions',
        {
			params:
			{
                filter: { where: { activationDate: { neq: null }, revocationDate: null }},
                access_token: this._token
			},
            timeout: 60000
        })
        .then(response =>
        {
            return response;
        })
        .catch(error =>
        {
            return Promise.reject(error);
        });
	}

    /**
    * Get a node's events and actions
    * Endpoint: /doors/get-events-and-actions
    * Type: GET
    *
    * @param {number} nodeId
    *
    * @returns {Promise}
    */
    getDoorEventsAndActions({ doorId, limit, skip })
	{
        let filter = {}

        if (limit != null)
            filter.limit = limit;
            
        if (skip != null)
            filter.skip = skip;

		return axios.get(`${API.URL}doors/get-events-and-actions/${doorId}`,
        {
			params:
			{
                filter,
            	access_token: this._token
			}
        })
        .then(response =>
        {
            return response;
        })
        .catch(error =>
        {
            console.log(JSON.stringify(error))
            return Promise.reject(error);
        });
	}

    /**
    * Triggers a door
    * Endpoint: /doors/trigger
    * Type: POST
    *
    * @param {number} doorId
    *
    * @returns {Promise}
    */
    triggerDoor(doorId, type = 'webapp')
	{        
		return axios.post(API.URL + 'doors/trigger?access_token=' + this._token,
        {
            id: doorId,
            type: this.isTesla() ? 'tesla' : type
        })
        .then(response =>
        {
            return response;
        })
        .catch(error =>
        {
            console.dir(error)
            return Promise.reject(error);
        });
	}
    
    /**
    * Deletes a door
    * Endpoint: /doors/{doorId}
    * Type: DELETE
    *
    * @param {number} doorId
    *
    * @returns {Promise}
    */
    deleteDoor(doorId)
	{
		return axios.delete(API.URL + 'doors/' + doorId + '?access_token=' + this._token)
        .then(response =>
        {
            return response;
        })
        .catch(error =>
        {
            return Promise.reject(error);
        });
	}

    /**
    * Uploads a door image
    * Endpoint: /doors/{doorId}/add-image
    * Type: POST
    *
    * @param {number} doorId
    *
    * @returns {Promise}
    */
    uploadDoorImage(doorId, image)
    {
        if (doorId == null || image == null || image.uri == null || image.type == null)
        {
            return Promise.reject("uploadDoorImage params are not correct");
        }
        else
        {
            if (image.fileName == null)
                image.fileName = 'door_image';
            
            let formData = new FormData();
            formData.append('data', { uri: image.uri, name: image.fileName, type: image.type });
            
            return axios.post(API.URL + 'doors/' + doorId + '/add-image?access_token=' + this._token, formData,
            {
                headers: {'Content-Type': 'multipart/form-data'}
            })
            .then(response =>
            {
                return response;
            })
            .catch(error =>
            {
                return Promise.reject(error);
            });
        }
    }
    
    /**
    * Get isOpen from door
    * Endpoint: /permissions
    * Type: GET
    *
    * @param {number} doorId
    *
    * @returns {Promise} with isOpen value and changeStatusAt
    */
    getDoorIsOpen(doorId)
	{
        return new Promise(async (resolve, reject) =>
        {
            try
            {
                let isOpen = null
                let changeStatusAt = null
                let res = await axios.get(`${API.URL}permissions`,
                            {
                                params:
                                {
                                    access_token: this._token,
                                    filter: `{"where":{"doorId":${doorId}}}`
                                }
                            })

                if (res == null || res.data == null ||
                    res.data.length === 0 ||
                    res.data[0].PermissionDoor == null)
                    reject('No permission or PermissionDoor')
                
                isOpen = (res.data[0].PermissionDoor.isOpen != null) ?
                            !!res.data[0].PermissionDoor.isOpen : null
                changeStatusAt = res.data[0].PermissionDoor.changeStatusAt
                
                resolve({ isOpen, changeStatusAt })
            }
            catch(error)
            {
                reject(error)
            }
        })
	}
    


    /**
    * Send feedback/suggestions/problems through the app
    * Endpoint: /feedbacks
    * Type: POST
    *
    * @param {string} message
    *
    * @returns {Promise}
    */
	sendFeedback(message)
	{
		return axios.post(API.URL + 'feedbacks?access_token=' + this._token,
        {
            comment: message
        })
        .then(response =>
        {
            return response;
        })
        .catch(error =>
        {
            return Promise.reject(error);
        });
	}




    /**
    * Get all houses for a logged user
    * Endpoint: /users/{userId}/houses
    * Type: GET
    *
    * @returns {Promise}
    */
	getUsersHouses()
	{
		return axios.get(`${API.URL}users/${this._userId}/houses`,
        {
			params:
			{
            	access_token: this._token
			}
        })
        .then(response =>
        {
            return response;
        })
        .catch(error =>
        {
            return Promise.reject(error);
        });
	}

    /**
    * Get all houses for a logged user with each house's hubs
    * Endpoint: /users/{userId}/houses
    * Type: GET
    *
    * @returns {Promise}
    */
	getUsersHousesWithHubs()
	{
		return axios.get(`${API.URL}users/${this._userId}/houses`,
        {
			params:
			{
            	access_token: this._token,
                filter: '{"include": ["hubs", "city"]}'
			}
        })
        .then(response =>
        {
            return response;
        })
        .catch(error =>
        {
            return Promise.reject(error);
        });
	}

    /**
    * Get all houses for a logged user with each house's doors
    * Endpoint: /users/{userId}/houses
    * Type: GET
    *
    * @returns {Promise}
    */
    getUsersHousesWithDoors()
    {
        return axios.get(`${API.URL}users/${this._userId}/houses`,
        {
			params:
			{
            	access_token: this._token,
                filter: '{"include": ["place", "hubs", "city", {"relation": "nodes", "scope": {"include": [{"relation": "actuators", "scope": {"include": ["door"]}}]}}]}'
			}
        })
        .then(response =>
        {
            return response;
        })
        .catch(error =>
        {
            return Promise.reject(error);
        });
    }

    /**
    * Get hubs for a house
    * Endpoint: /houses/{id}/hubs
    * Type: GET
    *
    * @param {number} houseId
    *
    * @returns {Promise}
    */
	getHubsForHouse({ houseId })
	{
		return axios.get(`${API.URL}houses/${houseId}/hubs`,
        {
			params:
			{
            	access_token: this._token
			}
        })
        .then(response =>
        {
            return response;
        })
        .catch(error =>
        {
            return Promise.reject(error);
        });
	}


	/********** Users requests **********/

    /**
    * Gets the permissions (virtual remotes) for a door
    * Endpoint: /doors/get-permissions
    * Type: GET
    *
    * @param {number} doorId
    *
    * @returns {Promise}
    */
	getPermissions(doorId)
	{
		return axios.get(API.URL + 'doors/get-permissions/' + doorId,
        {
			params:
			{
            	access_token: this._token
			}
        })
        .then(response =>
        {
            return response;
        })
        .catch(error =>
        {
            return Promise.reject(error);
        });
	}

    /**
    * Checks with the server what users (emails and phone numbers) have an account
    * Endpoint: /users/check-registered-users
    * Type: POST
    *
    * @param {array} contacts Array of contacts: [{ emailAddresses: [{ email: '' }], phoneNumbers: [{ number: '' }] }, ...]
    *
    * @returns {Promise}
    */
	checkRegisteredUsers(emailsArray)
	{
		var dataContacts = { contactsObject: [] }

        emailsArray.forEach(email =>
        {
            let c = {}
    		c.id = email.trim()
            c.emails = []
			c.emails.push(email.trim().toLowerCase())
            dataContacts.contactsObject.push(c)
        })

		return axios.post(API.URL + 'users/check-registered-users?access_token=' + this._token, dataContacts)
        .then(response =>
        {
            return response;
        })
        .catch(error =>
        {
            return Promise.reject(error);
        });
	}


    /**
    * Gives a permission (virtual remote) to a user for a door
    * Endpoint: /doors/grant-permission
    * Type: POST
    *
    * @param {object} data The permission data
    *
    * @returns {Promise}
    */
    grantPermission(data)
	{
		return axios.post(API.URL + 'doors/grant-permission?access_token=' + this._token, data)
        .then(response =>
        {
            return response;
        })
        .catch(error =>
        {
            return Promise.reject(error);
        });
	}

    /**
    * Revokes a permission (virtual remote) to a user for a door
    * Endpoint: /doors/revoke-permission
    * Type: POST
    *
    * @param {number} permissionId
    *
    * @returns {Promise}
    */
    revokePermission(permissionId)
	{
		return axios.post(API.URL + 'doors/revoke-permission?access_token=' + this._token,
        {
            permissionId: permissionId
        })
        .then(response =>
        {
            return response;
        })
        .catch(error =>
        {
            return Promise.reject(error);
        });
	}

    /**
    * Sends an email to inform a guest with no HH account that someone gave him
    * a virtual remote.
    * Endpoint: /permissions/{permissionId}/send-invitation-email
    * Type: POST
    *
    * @param {number} permissionId
    * @param {string} email Email of the guest to send the invitation email to
    *
    * @returns {Promise}
    */
    sendInvitationEmail(permissionId, email)
	{
		return axios.post(API.URL + 'permissions/' + permissionId + '/send-invitation-email?access_token=' + this._token,
        {
            guestEmail: email
        })
        .then(response =>
        {
            return response;
        })
        .catch(error =>
        {
            return Promise.reject(error);
        });
	}
    
    /**
    * Sends an SMS to inform a guest with no HH account that someone gave him
    * a virtual remote.
    * Endpoint: /permissions/{permissionId}/send-invitation-sms
    * Type: POST
    *
    * @param {number} permissionId
    *
    * @returns {Promise}
    */
    sendInvitationSms(permissionId, phone)
	{
		return axios.post(API.URL + 'permissions/' + permissionId + '/send-invitation-sms?access_token=' + this._token,
        {
            guestPhone: phone
        })
        .then(response =>
        {
            return response;
        })
        .catch(error =>
        {
            return Promise.reject(error);
        });
	}
    
    /**
    * Triggers a door
    * Endpoint: /permissions/link-pending-permission
    * Type: POST
    *
    * @param {number} permissionId
    * @param {string} token
    *
    * @returns {Promise}
    */
    linkPendingPermission(permissionId, token)
	{
		return axios.post(`${API.URL}permissions/${permissionId}/link-pending-permission?access_token=${this._token}`,
        {
            token: token
        })
        .then(response =>
        {
            return response;
        })
        .catch(error =>
        {
            return Promise.reject(error);
        });
	}

    /**
    * Transfers the administration of a house
    * Endpoint: /houses/{houseId}/transfer-ownership
    * Type: POST
    *
    * @param {number} houseId
    * @param {number} newAdminUserId
    *
    * @returns {Promise}
    */
    transferAdmin(houseId, newAdminUserId)
	{
		return axios.post(API.URL + 'houses/' + houseId + '/transfer-ownership?access_token=' + this._token,
        {
            userId: newAdminUserId
        })
        .then(response =>
        {
            return response;
        })
        .catch(error =>
        {
            return Promise.reject(error);
        });
	}


	getAutocompletePlaces(search)
	{
		return axios.get('https://maps.googleapis.com/maps/api/place/autocomplete/json',
        {
			params:
			{
				input: search,
            	key: 'AIzaSyA1xn24xE1TM4Bl32V-tx2_J8XV7arQwoY'
			}
        })
        .then(response =>
        {
            return response;
        })
        .catch(error =>
        {
            return Promise.reject(error);
        });
	}
    getPlaceDetails(placeId)
    {
        return axios.get('https://maps.googleapis.com/maps/api/place/details/json',
        {
			params:
			{
				placeid: placeId,
            	key: 'AIzaSyA1xn24xE1TM4Bl32V-tx2_J8XV7arQwoY'
			}
        })
        .then(response =>
        {
            return response;
        })
        .catch(error =>
        {
            return Promise.reject(error);
        });
    }


	/********** Houses / Hubs / Nodes Requests **********/

    /**
    * Gets the status of the hub (HH CONNECT): has Internet access or not.
    * Endpoint: /hubs/{id}}/status
    * Type: GET
    *
    * @param {number} hubId
    * @param {string} key
    *
    * @returns {Promise}
    */
	getHubStatus({ hubId, key })
	{
		return axios.get(`${API.URL}hubs/${hubId}/status`,
        {
			params:
			{
                options: { key },
            	access_token: this._token
			}
        })
        .then(response =>
        {
            return response;
        })
        .catch(error =>
        {
            return Promise.reject(error);
        });
	}

    /**
    * Creates a house.
    * Endpoint: /houses
    * Type: POST
    *
    * @param {string} houseName
    * @param {string} placeId Google Place Id of the house location
    *
    * @returns {Promise}
    */
    createHouse(houseName, houseType, placeId)
	{
		return axios.post(API.URL + 'houses?access_token=' + this._token,
        {
            name: houseName,
            type: houseType,
			googlePlaceId: placeId
        })
        .then(response =>
        {
            return response;
        })
        .catch(error =>
        {
            return Promise.reject(error);
        });
	}


    /**
    * Attaches a hub (HH CONNECT) to a house.
    * Endpoint: /hubs/{id}
    * Type: PATCH
    *
    * @param {number} hubMacId
    * @param {string} encKey
    * @param {number} houseId
    *
    * @returns {Promise}
    */
    attachHubToHouse(hubMacId, encKey, houseId)
	{
		return axios.patch(`${API.URL}hubs/${hubMacId}?access_token=${this._token}`,
        {
            houseId: houseId,
            key: encKey
        })
        .then(response =>
        {
            return response;
        })
        .catch(error =>
        {
            return Promise.reject(error);
        });
	}

    /**
    * Attaches a node (HH GARAGE) to a house.
    * Endpoint: /nodes/{id}
    * Type: PATCH
    *
    * @param {string} nodeMacId
    * @param {number} houseId
    *
    * @returns {Promise}
    */
    attachNodeToHouse(nodeMacId, hubId, houseId)
	{
		return axios.patch(`${API.URL}nodes/${nodeMacId}?access_token=${this._token}`,
        {
            hubId: hubId,
            houseId: houseId
        })
        .then(response =>
        {
            return response;
        })
        .catch(error =>
        {
            return Promise.reject(error);
        });
	}

    /**
    * Gets a node.
    * Endpoint: /houses/{id}/nodes
    * Type: GET
    *
    * @param {string} nodeId
    *
    * @returns {Promise}
    */
    getNode({ houseId, nodeId })
	{
		return axios.get(API.URL + 'houses/' + houseId + '/nodes',
        {
			params:
			{
            	access_token: this._token,
                filter:
                {
                	where:
                	{
                		id: nodeId
                	},
                	include:
                    [
                		{
                			relation: "actuators",
                			scope:
                			{
                				include:
                                [
                                    "door",
                					{
                						relation: "actuatorTypeAssembly",
                						scope:
                						{
                							include: "actuatorType"
                						}
                                    }
                                ]
                            }
                        },
                        {
                            relation: "sensors",
                            scope:
                            {
                                include:
                                [
                                    {
                						relation: "sensorTypeAssembly",
                						scope:
                						{
                							include: "sensorType"
                						}
                                    }
                                ]
                            }
                        }
                    ]
                }
			}
        })
        .then(response =>
        {
            return response;
        })
        .catch(error =>
        {
            return Promise.reject(error);
        });
	}

    /**
    * Gets the actuators of a node.
    * Endpoint: /nodes/{nodeId}/actuators
    * Type: GET
    *
    * @param {string} nodeId
    *
    * @returns {Promise}
    */
    getActuators({ nodeId })
	{
		return axios.get(API.URL + 'nodes/' + nodeId + '/actuators',
        {
			params:
			{
            	access_token: this._token,
                filter: '{"include": ["door", {"relation": "actuatorTypeAssembly", "scope": {"include": ["actuatorType"]}}]}'
			}
        })
        .then(response =>
        {
            return response;
        })
        .catch(error =>
        {
            return Promise.reject(error);
        });
	}

    /**
    * Creates a door.
    * Endpoint: /doors
    * Type: POST
    *
    * @param {string} name
    * @param {number} actuatorId
    * @param {number} sensorId
    * @param {string} actionData
    *
    * @returns {Promise}
    */
    createDoor({ name, actuatorId, sensorId, actionData })
	{
        let data =
        {
            name,
			actuatorId,
            sensorId
        }

        if (actionData != null)
            data.actionData = actionData;

		return axios.post(API.URL + 'doors?access_token=' + this._token, data)
        .then(response =>
        {
            return response;
        })
        .catch(error =>
        {
            return Promise.reject(error);
        });
	}

    /**
    * Updates a door.
    * Endpoint: /doors/{doorId}
    * Type: PATCH
    *
    * @param {number} doorId
    * @param {string} name
    * @param {number} actuatorId
    * @param {number} sensorId
    *
    * @returns {Promise}
    */
    updateDoor({ doorId, name, actuatorId, sensorId, actionData })
	{
        let fields = {}
        if (name != null) fields.name = name;
        if (actuatorId != null) fields.actuatorId = actuatorId;
        if (sensorId != null) fields.sensorId = sensorId;

        if (actionData != null)
            fields.actionData = actionData;

		return axios.patch(API.URL + 'doors/' + doorId + '?access_token=' + this._token, fields)
        .then(response =>
        {
            return response;
        })
        .catch(error =>
        {
            return Promise.reject(error);
        });
	}

    /**
    * Updates a door.
    * Endpoint: /nodes/{nodeId}/remote
    * Type: POST
    *
    * @param {number} nodeId
    * @param {boolean} listenModeOn
    *
    * @returns {Promise}
    */
    setGarageInListenMode({ nodeId, listenModeOn })
	{
		return axios.post(API.URL + 'nodes/' + nodeId + '/remote?access_token=' + this._token,
        {
            remote: listenModeOn ? 1 : 0
        })
        .then(response =>
        {
            return response;
        })
        .catch(error =>
        {
            return Promise.reject(error);
        });
	}


    /**
    * Checks reachability of a hub.
    * Endpoint: /hubs/{hubId}/status
    * Type: GET
    *
    * @param {number} hubId
    *
    * @returns {Promise}
    */
    checkConnectStatus(hubId)
    {
        return axios.get(API.URL + 'hubs/' + hubId + '/status?access_token=' + this._token)
        .then(response =>
        {
            return response;
        })
        .catch(error =>
        {
            return Promise.reject(error);
        });
    }

    /**
    * Checks reachability of a node.
    * Endpoint: /nodes/{nodeId}/status
    * Type: GET
    *
    * @param {string} nodeId
    *
    * @returns {Promise}
    */
    checkGarageStatus(nodeId)
    {
        return axios.get(API.URL + 'nodes/' + nodeId + '/status?access_token=' + this._token)
        .then(response =>
        {
            return response;
        })
        .catch(error =>
        {
            return Promise.reject(error);
        });
    }

    
    
    /********** App installation **********/
    
    // sendAppInstallation()
    // {
    //     var url = API.URL + 'installations/register?access_token=' + this._token;

	// 	axios.post(url,
    //     {
	// 		appId: DeviceInfo.getUniqueID(),
	// 		deviceToken: '-',
	// 		deviceType: Platform.OS,

	// 		appVersion: DeviceInfo.getVersion(),
	// 		localeCode: DeviceInfo.getDeviceLocale(),
	// 		deviceManufacturer: DeviceInfo.getManufacturer(),
	// 		deviceModel: (Platform.OS === 'ios') ? DeviceInfo.getDeviceId() : DeviceInfo.getModel(),
	// 		osVersion: DeviceInfo.getSystemVersion()
    //     })
    //     .then(response =>
    //     {
	// 		console.log(response);
    //     })
    //     .catch(error =>
    //     {
	// 		console.log(error.response);
    //     });
    // }
    
    
    /********** User profile Requests **********/
    
    /**
    * Updates a user.
    * Endpoint: /users/{id}
    * Type: PATCH
    *
    * @param {number} userId
    * @param {object} data
    *
    * @returns {Promise}
    */
    updateUser(userId, data)
	{
		return axios.patch(`${API.URL}users/${userId}?access_token=${this._token}`, data)
        .then(response =>
        {
            return response;
        })
        .catch(error =>
        {
            return Promise.reject(error);
        });
	}
    
    /**
    * Updates a user's email (sends a 6 digit code).
    * Endpoint: /users/change-email
    * Type: POST
    *
    * @param {string} email
    *
    * @returns {Promise}
    */
    changeUserEmail(email)
	{
		return axios.post(`${API.URL}users/change-email?access_token=${this._token}`,
        `email=${email}`)
        .then(response =>
        {
            return response;
        })
        .catch(error =>
        {
            return Promise.reject(error);
        });
	}
    
    /**
    * Verifies the update on a user's email through a 6 digit code.
    * Endpoint: /users/verify-change-email
    * Type: POST
    *
    * @param {string} email
    *
    * @returns {Promise}
    */
    verifyChangeUserEmail(email, code)
	{
		return axios.post(`${API.URL}users/verify-change-email?access_token=${this._token}`,
        `email=${email}&code=${code}`)
        .then(response =>
        {
            return response;
        })
        .catch(error =>
        {
            return Promise.reject(error);
        });
	}
    
    /**
    * Updates a user's phone (sends a 6 digit code).
    * Endpoint: /users/change-phone
    * Type: POST
    *
    * @param {string} phone
    *
    * @returns {Promise}
    */
    changeUserPhone(phone)
	{
		return axios.post(`${API.URL}users/change-phone?access_token=${this._token}`,
        `phone=${phone.replace('+', '%2B')}`)
        .then(response =>
        {
            return response;
        })
        .catch(error =>
        {
            return Promise.reject(error);
        });
	}
    
    /**
    * Verifies the update on a user's phone through a 6 digit code.
    * Endpoint: /users/verify-change-phone
    * Type: POST
    *
    * @param {string} phone
    *
    * @returns {Promise}
    */
    verifyChangeUserPhone(phone, code)
	{
		return axios.post(`${API.URL}users/verify-change-phone?access_token=${this._token}`,
        `phone=${phone.replace('+', '%2B')}&code=${code}`)
        .then(response =>
        {
            return response;
        })
        .catch(error =>
        {
            return Promise.reject(error);
        });
	}
    
    
    /**
    * Generates a 6 digit verification code to authorize a new device.
    * Endpoint: /UserDevices/start-verification
    * Type: POST
    *
    * @param {string} email
    *
    * @returns {Promise}
    */
    startNewDeviceVerification(email)
	{
		return axios.post(`${API.URL}UserDevices/start-verification`,
        `email=${email}&deviceId=${this.getDeviceId(email)}&deviceType=${this.getDeviceType()}&via=phone`)
        .then(response =>
        {
            return response;
        })
        .catch(error =>
        {
            return Promise.reject(error);
        });
	}
    
    /**
    * Verifies a 6 digit code for a new device.
    * Endpoint: /UserDevices/verify
    * Type: POST
    *
    * @param {string} email
    * @param {string} code
    *
    * @returns {Promise}
    */
    verifyNewDevice(email, code)
	{
		return axios.post(`${API.URL}UserDevices/verify`,
        `email=${email}&deviceId=${this.getDeviceId(email)}&deviceType=${this.getDeviceType()}&code=${code}`)
        .then(response =>
        {
            return response;
        })
        .catch(error =>
        {
            return Promise.reject(error);
        });
	}
    
    /**
    * Start the user's password change.
    * Endpoint: /users/change-password
    * Type: POST
    *
    * @param {string} email
    * @param {string} via
    *
    * @returns {Promise}
    */
    changePassword(email, via)
	{
		return axios.post(`${API.URL}users/change-pass`,
        `email=${email}&via=${via}`)
        .then(response =>
        {
            return response;
        })
        .catch(error =>
        {
            return Promise.reject(error);
        });
	}
    
    /**
    * Verify the user's password change.
    * Endpoint: /users/verify-change-password
    * Type: POST
    *
    * @param {string} email
    * @param {string} code
    * @param {string} password
    *
    * @returns {Promise}
    */
    verifyChangePassword(email, code, password)
	{
		return axios.post(`${API.URL}users/verify-change-password`,
        `email=${email}&code=${code}&password=${password}`)
        .then(response =>
        {
            return response;
        })
        .catch(error =>
        {
            return Promise.reject(error);
        });
	}
    
    
    
    /*********** Virtual Remotes ***********/
    assignVirtualRemote(virtualRemoteId, doorId, userId)
    {
        let params =
        {
            access_token: this._token,
            virtualRemoteId,
            doorId,
            userId
        }
        
        return axios.get(API.URL + 'virtualRemotes/assign-virtual-remote', { params })
    }
    giveVirtualRemotes(virtualRemoteIds, doorId, userId)
    {
        let params =
        {
            access_token: this._token,
            virtualRemoteId: virtualRemoteIds.toString(),
            doorId,
            userId
        }
        
        return axios.get(API.URL + 'virtualRemotes/give-virtual-remote', { params })
    }
    reserveVirtualRemotes(permissionId, virtualRemoteIds)
    {
        let params =
        {
            access_token: this._token,
            permissionId,
            virtualRemoteId: virtualRemoteIds.toString()
        }
        
        return axios.get(API.URL + 'virtualRemotes/reserve-virtual-remote', { params })
    }
    getVirtualRemotesByHouse(houseId)
    {
        let params =
        {
            access_token: this._token,
            houseId
        }
        
        return axios.get(API.URL + 'virtualRemotes/get-virtual-remotes-by-house', { params })
    }
    getVirtualRemotesByDoor(doorId)
    {
        let params =
        {
            access_token: this._token,
            doorId
        }
        
        return axios.get(API.URL + 'virtualRemotes/get-virtual-remotes-by-door', { params })
    }
    
    
    /*********** Virtual Remotes Subscriptions ***********/
    getStripeProducts()
    {
        let params =
        {
            access_token: this._token
        }
        
        return axios.get(`${API.URL}VirtualRemoteSubscriptions/get-products`, { params })
    }
    createCheckoutSession(customerEmail, houseId, priceId, trial)
    {
        let params = { access_token: this._token }
        let data = { customerEmail, houseId, priceId, trial }
        
        return axios.post(`${API.URL}VirtualRemoteSubscriptions/create-buy-checkout-session`,
                data,
                { params })
    }
    createPortalSession(customerEmail, houseId)
    {
        let params = { access_token: this._token }
        let data = { customerEmail }
        if (houseId != null)
            data.houseId = houseId
        
        return axios.post(`${API.URL}VirtualRemoteSubscriptions/create-portal-session`,
                data,
                { params })
    }
    resumeSubscription(subscriptionId)
    {
        let params =
        {
            subscriptionId,
            access_token: this._token
        }
        
        return axios.get(`${API.URL}VirtualRemoteSubscriptions/resume-subscription`, { params })
    }
    createPaymentSession(subscriptionId, houseId, customerEmail)
    {
        let params =
        {
            subscriptionId,
            houseId,
            customerEmail,
            access_token: this._token
        }
        
        return axios.get(`${API.URL}VirtualRemoteSubscriptions/create-setup-payment-session`, { params })
    }
    cancelSubscription(subscriptionId)
    {
        let params =
        {
            subscriptionId,
            access_token: this._token
        }
        
        return axios.get(`${API.URL}VirtualRemoteSubscriptions/cancel-subscription`, { params })
    }
    
    
    /************* QR Login  *************/
	getQrLoginToken()
	{
        let params = {
            deviceType: 'tesla',
            deviceId: this.getDeviceId()
        }
        
		return axios.get(API.URL + 'users/get-login-token', { params })
	}
    getQrAccessToken(timestamp, token)
	{
        let data = {
            deviceType: 'tesla',
            deviceId: this.getDeviceId(),
            timestamp: `${timestamp}`,
            token
        }
        
		return axios.post(API.URL + 'users/get-access-token', data)
	}
    
    
    
    /**
     * All in one get request for the application
     * Type: GET
     *
     * @returns {Promise} A promise
     */
    GET (path, params = {}, id = null, timeout = 10000, access_token = true)
    {
        if (access_token) {
            params['access_token'] = this._token
        }

        return new Promise((resolve, reject) =>
        {
            axios.get(`${API.URL}${path}` + (id ? `/${id}` : ``),
                {
                    params: params,
                    timeout
                })
                .then(response =>
                {
                    resolve(response.data);
                })
                .catch(error =>
                {
                    reject(error);
                });
        });
    }
}
